import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisProjectFilterInput,
	type ArtemisProjectSortInput,
	CreateProjectDocument,
	type CreateProjectInput,
	DeleteProjectDocument,
	GetProjectDocument,
	GetProjectsDocument,
	UpdateProjectDocument,
	type UpdateProjectInput,
} from "@/common/artemis-sdk/graphql";
import type { Project } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Fetches the first page of projects for a workspace (array form, for lookups/dropdowns).
 * For the paginated list use {@link useProjectsPaged}.
 */
export function useProjects(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["projects", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetProjectsDocument, { workspaceId });
			return (data.projects?.nodes as Project[]) ?? [];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Server-side paginated project list for a workspace: Next/Prev cursor pagination plus
 * server-side search (name/description/id), status/type filters, and sort.
 */
export function useProjectsPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return usePagedList<Project>({
		baseKey: ["projects", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetProjectsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisProjectSortInput>(sort),
				where: buildWhere(
					debouncedSearch,
					["name", "description", "id"],
					filters,
				) as ArtemisProjectFilterInput | undefined,
			});
			const c = data.projects;
			return {
				nodes: (c?.nodes as Project[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/**
 * Fetches a single project by its ID. Pass `undefined` (e.g. before the caller has one yet,
 * such as a form field not filled in) to skip the query — it stays disabled until a real ID
 * is provided rather than firing with an invalid one.
 */
export function useProject(projectId?: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["project", projectId],
		queryFn: async () => {
			const data = await gqlFetch(GetProjectDocument, {
				projectId: projectId || "",
			});
			return data.project as Project;
		},
		enabled: !!projectId,
	});
}

/** Creates a new project. */
export function useCreateProject() {
	const gqlFetch = useGqlClient();

	const { mutate } = useMutation({
		mutationFn: async (input: CreateProjectInput) => {
			const data = await gqlFetch(CreateProjectDocument, { input });
			return data.createProject.project;
		},
	});
	return mutate;
}

/** Updates an existing project. Invalidates the project query on success. */
export function useUpdateProject(projectId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: async (input: UpdateProjectInput) => {
			const data = await gqlFetch(UpdateProjectDocument, { projectId, input });
			return data.updateProject.project;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["project", projectId] });
		},
	});
	return mutate;
}

/** Deletes a project. Invalidates the project query on success. */
export function useDeleteProject(projectId: string) {
	const queryClient = useQueryClient();
	const gqlFetch = useGqlClient();

	const { mutate } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(DeleteProjectDocument, {
				projectId,
			});
			return data.deleteProject.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["project", projectId],
			});
		},
	});
	return mutate;
}
